import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { trackPageView, isAdminPath } from '../utils/analytics';

const BRAND = 'Celestial Stones';

/* Route → the name the page shows up under in Google Analytics. Ordered:
   the first match wins, so put specific paths above their parents. */
const PAGES = [
  [/^\/$/, 'Home', 'home'],
  [/^\/shop/, 'Shop', 'shop'],
  [/^\/cart/, 'Cart', 'cart'],
  [/^\/planets/, 'Shop by Planet', 'planets'],
  [/^\/blog\/.+/, 'Blog Post', 'blog'],
  [/^\/blog/, 'Blog', 'blog'],
  [/^\/privacy-policy/, 'Privacy Policy', 'legal'],
  [/^\/terms-of-service/, 'Terms & Conditions', 'legal'],
  [/^\/shipping-returns/, 'Shipping & Returns', 'legal'],
  [/^\/account\/login/, 'Sign In', 'account'],
  [/^\/account\/orders/, 'My Orders', 'account'],
  [/^\/account\/consultations/, 'My Consultations', 'account'],
  [/^\/account\/profile/, 'My Profile', 'account'],
  [/^\/account\/password/, 'Change Password', 'account'],
  [/^\/account/, 'My Account', 'account'],
];

/* "vedic-gems-for-saturn" → "Vedic Gems For Saturn". Every post would
   otherwise report as the same "Blog Post" row. */
const fromSlug = (slug) =>
  slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const describe = (pathname) => {
  const match = PAGES.find(([pattern]) => pattern.test(pathname));

  if (!match) return { title: pathname, section: 'other' };

  const [, title, section] = match;

  if (section === 'blog' && pathname.startsWith('/blog/')) {
    return { title: fromSlug(pathname.slice('/blog/'.length)), section };
  }

  return { title, section };
};

/**
 * Sends a GA4 page view on every route change.
 *
 * A single-page app never reloads, so gtag's own page view fires once and
 * every route after that would be invisible. Mounted inside the router in
 * App.js; renders nothing.
 */
export default function AnalyticsListener() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (isAdminPath(pathname)) return;

    const { title, section } = describe(pathname);

    // Keep the browser tab and the GA report saying the same thing.
    document.title = pathname === '/' ? `${BRAND} — Premium Astrological Gemstones` : `${title} — ${BRAND}`;

    trackPageView({
      // The query string is part of the path: /shop?planet=Venus is a
      // different report row from /shop.
      path: `${pathname}${search}`,
      title,
      section,
    });
  }, [pathname, search]);

  return null;
}
